<template>
  <div class="map-container" :style="{ width: width, height: height }">
    <div v-if="errorMsg" class="error-message">
      {{ errorMsg }}
    </div>
    <div v-else ref="mapContainer" class="map"></div>
  </div>
</template>

<script>
export default {
  name: 'BaiduMap',
  props: {
    address: {
      type: String,
      required: true
    },
    height: {
      type: String,
      default: '300px'
    },
    width: {
      type: String,
      default: '100%'
    },
    zoom: {
      type: Number,
      default: 15
    },
    ak: {
      type: String,
      default: 'BaJ4rCvfnEUuG5iMWtIRBjv6cHmfRI6R'
    },
    title: {
      type: String,
      default: '公司地址'
    }
  },
  data() {
    return {
      mapInstance: null,
      marker: null,
      point: null,
      isMapLoaded: false,
      isScriptLoaded: false,
      errorMsg: ''
    };
  },
  watch: {
    address(newAddress) {
      if (newAddress && this.isMapLoaded) {
        this.geocodeAddress(newAddress);
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initMap();
    });
  },
  methods: {
    loadBaiduMapScript() {
      if (window.BMap) {
        this.isScriptLoaded = true;
        return Promise.resolve();
      }

      return new Promise((resolve, reject) => {
        // 创建全局回调函数
        window.baiduMapInit = () => {
          this.isScriptLoaded = true;
          resolve();
        };

        const script = document.createElement('script');
        script.type = 'text/javascript';
        // 百度地图 v3.0 的加载方式，设置 callback
        script.src = `https://api.map.baidu.com/api?v=3.0&ak=${this.ak}&callback=baiduMapInit`;
        script.onerror = () => {
          this.errorMsg = '百度地图脚本加载失败';
          this.$emit('error', this.errorMsg);
          reject(new Error('百度地图脚本加载失败'));
        };
        document.head.appendChild(script);
      });
    },

    async initMap() {
      if (!this.$refs.mapContainer) return;

      try {
        if (!this.isScriptLoaded) {
          await this.loadBaiduMapScript();
        }

        // 创建地图实例
        this.mapInstance = new window.BMap.Map(this.$refs.mapContainer);

        // 设置地图中心点和缩放级别
        const defaultPoint = new window.BMap.Point(123.429096, 41.796767); // 默认沈阳坐标
        this.mapInstance.centerAndZoom(defaultPoint, this.zoom);

        // 添加地图控件
        this.mapInstance.addControl(new window.BMap.NavigationControl());
        this.mapInstance.addControl(new window.BMap.ScaleControl());
        this.mapInstance.enableScrollWheelZoom();

        // 地理编码
        if (this.address) {
          this.geocodeAddress(this.address);
        }

        this.isMapLoaded = true;
      } catch (error) {
        console.error('初始化地图失败:', error);
        this.errorMsg = '初始化地图失败';
        this.$emit('error', this.errorMsg);
      }
    },

    geocodeAddress(address) {
      if (!address || !this.mapInstance) return;

      // 创建地址解析器实例
      const myGeo = new window.BMap.Geocoder();

      // 将地址解析结果显示在地图上，并调整地图视野
      myGeo.getPoint(address, (pt) => {
        if (pt) {
          this.point = { lng: pt.lng, lat: pt.lat };

          // 清除之前的标记
          if (this.marker) {
            this.mapInstance.removeOverlay(this.marker);
          }

          // 创建标记
          this.marker = new window.BMap.Marker(pt);
          this.mapInstance.addOverlay(this.marker);

          // 设置地图中心点
          this.mapInstance.centerAndZoom(pt, this.zoom);

          const offset = new window.BMap.Size(10, -25);
          const label = new window.BMap.Label(`${this.title} (点击跳转地图)`, { offset });
          label.setStyle({
            border       : "1px solid #666",
            background   : "rgba(255,255,255,0.9)",
            padding      : "2px 5px",
            fontSize     : "12px",
            color        : "#333",
            whiteSpace   : "nowrap",
            borderRadius : "4px",
            boxShadow    : "0 1px 4px rgba(0,0,0,0.2)"
          });

          this.marker.setLabel(label);

          // 添加点击事件，跳转到百度地图查看
          this.marker.addEventListener('click', () => {
            const url = `https://api.map.baidu.com/marker?location=${pt.lat},${pt.lng}&title=${encodeURIComponent(this.title)}&content=${encodeURIComponent(address)}&output=html&src=lnzc_asset`;
            window.open(url, '_blank');
          });
        } else {
          console.warn('无法解析地点:', address);
          this.errorMsg = '无法解析当前地址位置';
          this.$emit('error', this.errorMsg);
        }
      }, '中国');
    }
  }
};
</script>

<style lang="scss" scoped>
.map-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.map {
  width: 100%;
  height: 100%;
}

.error-message {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  background-color: #f5f5f5;
  color: #ff4d4f;
  font-size: 14px;
  text-align: center;
  padding: 20px;
}
</style>
